import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { FeedbackQuestionPageStyles as S } from '~/pages/communication/libs/FeedbackQuestionPage/styles'
import { parseEmojiOption } from '~/pages/communication/libs/parseEmojiOption'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { TPageProps } from '~/shared/types/commonTypes'

interface IOption {
  key: string
  i18nKey: string
}

interface IFeedback {
  badge: string
  text: string
}

interface IFeedbackQuestionPageProps extends TPageProps {
  question: string
  titleI18nKey: string
  optionI18nKeyPrefix: string
  options: readonly IOption[]
  feedback: Record<string, IFeedback>
  ns?: string
  contentGap?: number
}

const parseBadge = (text: string): { label: string; icon: string } => {
  const lastSpace = text.lastIndexOf(' ')
  if (lastSpace === -1) return { label: text, icon: '' }
  return { label: text.slice(0, lastSpace), icon: text.slice(lastSpace + 1) }
}

const FeedbackQuestionPage = ({
  nextPagePath,
  pageId,
  question,
  titleI18nKey,
  optionI18nKeyPrefix,
  options,
  feedback,
  ns = 'communication',
  contentGap,
}: IFeedbackQuestionPageProps) => {
  const { t } = useTranslation(ns)
  const [answer, setAnswer] = useState('')

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question,
  })

  const currentFeedback = answer ? feedback[answer] : null

  return (
    <S.Root>
      <S.ContentWrapper $gap={contentGap}>
        <S.Title>
          <Trans ns={ns} i18nKey={titleI18nKey} />
        </S.Title>

        <S.BottomGroup>
          {currentFeedback && (
            <S.FeedbackBlock>
              <S.FeedbackBadge>
                {(() => {
                  const { label, icon } = parseBadge(t(currentFeedback.badge))
                  return (
                    <>
                      <S.FeedbackBadgeText>{label}</S.FeedbackBadgeText>
                      {icon && (
                        <S.FeedbackBadgeIcon>{icon}</S.FeedbackBadgeIcon>
                      )}
                    </>
                  )
                })()}
              </S.FeedbackBadge>
              <S.FeedbackText>{t(currentFeedback.text)}</S.FeedbackText>
            </S.FeedbackBlock>
          )}

          <S.Options>
            {options.map(({ key, i18nKey }) => {
              const { emoji, label } = parseEmojiOption(
                t(`${optionI18nKeyPrefix}.${i18nKey}`),
              )

              return (
                <S.OptionButton
                  key={key}
                  type="button"
                  $isSelected={answer === key}
                  onClick={() => setAnswer(key)}
                >
                  <S.OptionEmoji>{emoji}</S.OptionEmoji>
                  <S.OptionLabel>{label}</S.OptionLabel>
                </S.OptionButton>
              )
            })}
          </S.Options>
        </S.BottomGroup>

        <S.Button
          type="button"
          disabled={!answer}
          onClick={() => handleContinue(answer)}
        >
          <Trans i18nKey="actions.continue" />
        </S.Button>
      </S.ContentWrapper>
    </S.Root>
  )
}

export default FeedbackQuestionPage
